import clsx from 'clsx';
import React, { useRef, useState } from 'react';
import { Link, matchPath, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { useTheme } from '../../../context/ThemeContext';
import {
  Avatar,
  IconButton,
  IconCalendar,
  IconChevronDown,
  IconGrid,
  IconMenu,
  IconMoon,
  IconPlug,
  IconSettings,
  IconSun,
  Menu,
  MenuItem,
  MenuLabel,
  MenuSeparator,
  NavCount,
} from '../../../ui/v2';
import type { PropertySummary } from '../api';
import { HOTEL_BASE, HOTEL_PROFILE_PATH, hotelPaths } from '../model';
import { pickHotelGroup, useHotelOrganization } from '../organization';
import { useProperties, useStaysCount, useStoreGroups } from '../queries';
import { ExperienceSwitcher } from './ExperienceSwitcher';
import {
  type HotelSection,
  PropertySwitcher,
  hotelPropertyParamOf,
  hotelSectionOf,
} from './PropertySwitcher';
import { forgetHotelPath, lastHotelPath } from './experiences';

/**
 * The hotel column (components doc §2). Same navy, same metrics as the
 * restaurant sidebar; the `v2-nav-hotel` class remaps the accent to teal
 * inside it, and that is the only visual difference besides the glyphs.
 *
 * Renders for every `/hotel/*` URL, including the group picker and the
 * loading/error bodies, so it must stand without a property: the switcher
 * shows a skeleton, the nav rows go inert until an id is known.
 */

/** What the sidebar derives from URL + cached queries. Exposed for tests. */
export interface HotelSidebarModel {
  organizationId: number | null;
  /** undefined while pending or failed. */
  properties: PropertySummary[] | undefined;
  propertiesPending: boolean;
  /** The property the URL names, if it is in the list. */
  current: PropertySummary | null;
  /**
   * The property the switcher displays: `current`; on Profile, the property
   * page the user left; else the first.
   */
  shown: PropertySummary | null;
  picksGroup: boolean;
  section: HotelSection;
}

/** Pure: the sidebar's read of a pathname and a property list. */
export function hotelSidebarModel(
  pathname: string,
  properties: PropertySummary[] | undefined,
  scope: {
    organizationId: number | null;
    picksGroup: boolean;
    pending: boolean;
    /** The remembered `av2.experience.hotelPath`, if any. */
    lastPropertyPath?: string | null;
  },
): HotelSidebarModel {
  // Matched as `PropertyScope` matches it, so the sidebar and the page agree.
  const find = (param: string | null) =>
    param === null
      ? null
      : (properties?.find((p) => String(p.id) === param) ?? null);
  const param = hotelPropertyParamOf(pathname);
  const current = find(param);
  // Profile is a detour from the property page the user left: keep showing
  // it. Otherwise no property in the URL (`/hotel`) shows the first, which
  // is where `/hotel` is about to land. A stale or malformed id shows
  // "Select property".
  const left =
    matchPath(HOTEL_PROFILE_PATH, pathname) && scope.lastPropertyPath
      ? find(hotelPropertyParamOf(scope.lastPropertyPath))
      : null;
  const shown =
    current ?? (param === null ? (left ?? properties?.[0] ?? null) : null);
  return {
    organizationId: scope.organizationId,
    properties,
    propertiesPending: scope.pending,
    current,
    shown,
    picksGroup: scope.picksGroup,
    section: hotelSectionOf(pathname),
  };
}

export interface HotelNavItem {
  key: HotelSection;
  label: string;
  icon: React.ReactNode;
  to: (propertyId: number) => string;
  /** Exact match for Overview; the section (a prefix) for the others. */
  isActive: (pathname: string, propertyId: number) => boolean;
  countWhat?: string;
}

export const HOTEL_NAV: HotelNavItem[] = [
  {
    key: 'overview',
    label: 'Overview',
    icon: <IconGrid />,
    to: hotelPaths.overview,
    isActive: (pathname, id) => pathname === hotelPaths.overview(id),
  },
  {
    key: 'stays',
    label: 'Stays',
    icon: <IconCalendar />,
    to: hotelPaths.stays,
    // Covers Stay detail and Guest (IA §7.5).
    isActive: (pathname) => hotelSectionOf(pathname) === 'stays',
    countWhat: 'on hold',
  },
  {
    key: 'integrations',
    label: 'Integrations',
    icon: <IconPlug />,
    to: hotelPaths.integrations,
    isActive: (pathname) => hotelSectionOf(pathname) === 'integrations',
  },
  {
    key: 'settings',
    label: 'Settings',
    icon: <IconSettings />,
    to: hotelPaths.settings,
    isActive: (pathname) => hotelSectionOf(pathname) === 'settings',
  },
];

const ON_HOLD = { status: 'on_hold' } as const;

export interface HotelSidebarProps {
  /** Closes the drawer after a navigation. Absent when pinned. */
  onNavigate?: () => void;
  /** Hides the pinned sidebar. Absent in the drawer. */
  onCollapse?: () => void;
}

export const HotelSidebar: React.FC<HotelSidebarProps> = ({
  onNavigate,
  onCollapse,
}) => {
  const { pathname } = useLocation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const scope = useHotelOrganization();
  const properties = useProperties(scope.organizationId);
  const groups = useStoreGroups({ enabled: scope.picksGroup });
  const model = hotelSidebarModel(pathname, properties.data, {
    organizationId: scope.organizationId,
    picksGroup: scope.picksGroup,
    pending:
      scope.pending || (scope.organizationId !== null && properties.isPending),
    lastPropertyPath: lastHotelPath(user?.id),
  });
  const propertyId = model.current?.id ?? null;
  // Profile names no property but, unlike `/hotel` or the group picker, is
  // not on the way anywhere: its rows lead back to the property it shows.
  const linkId =
    propertyId ??
    (pathname === HOTEL_PROFILE_PATH ? (model.shown?.id ?? null) : null);
  // Same key as the Overview's "On hold" tile, so React Query dedupes it.
  const openHolds = useStaysCount(scope.organizationId, propertyId, ON_HOLD);

  // A platform admin changes groups through the switcher, so it stays even
  // when the group has no hotels or none is chosen yet.
  const showSwitcher =
    model.picksGroup ||
    model.propertiesPending ||
    (model.properties?.length ?? 0) > 0;
  const chooseGroup = (id: number) => {
    if (user) {
      pickHotelGroup(user.id, id);
      // The remembered property page belongs to the previous group.
      forgetHotelPath(user.id);
    }
    onNavigate?.();
    navigate(HOTEL_BASE, { replace: true });
  };

  return (
    <aside className="v2-nav v2-nav-hotel flex h-full w-[240px] shrink-0 flex-col">
      {/* A. header */}
      <div className="v2-inverse flex h-16 items-center gap-1 px-3">
        {onCollapse && (
          <IconButton label="Hide navigation" onClick={onCollapse}>
            <IconMenu />
          </IconButton>
        )}
        <Link
          to={HOTEL_BASE}
          onClick={onNavigate}
          aria-label="AskArthur hotel home"
          className={clsx('inline-flex items-center', !onCollapse && 'px-1')}
        >
          {/* The light-on-dark mark: the navigation is navy in both themes. */}
          <img
            src="/images/logo/logo-full-dark.png"
            alt=""
            className="h-8 w-auto"
          />
        </Link>
      </div>

      {/* B. experience (dual-mode only; renders nothing otherwise) */}
      <ExperienceSwitcher current="hotel" onNavigate={onNavigate} />

      {/* C. scope: the property, and for a platform admin the group too */}
      {showSwitcher && (
        <PropertySwitcher
          properties={
            model.propertiesPending ? undefined : (model.properties ?? [])
          }
          current={model.shown}
          section={model.section}
          onNavigate={onNavigate}
          {...(model.picksGroup && {
            groups: groups.data,
            groupsFailed: groups.isError,
            currentGroupId: model.organizationId,
            onPickGroup: chooseGroup,
          })}
        />
      )}

      {/* E. nav: four items, no search, no group label */}
      <nav
        className="v2-inverse v2-scroll mt-3 flex-1 overflow-y-auto px-3 pb-3"
        aria-label="Hotel"
      >
        <div className="flex flex-col gap-px">
          {HOTEL_NAV.map((item) => (
            <HotelNavRow
              key={item.key}
              item={item}
              propertyId={linkId}
              pathname={pathname}
              count={item.key === 'stays' ? openHolds.data?.count : undefined}
              onNavigate={onNavigate}
            />
          ))}
        </div>
      </nav>

      {/* F. user */}
      <HotelUserMenu onNavigate={onNavigate} />
    </aside>
  );
};

const rowClass =
  'group relative flex h-9 items-center gap-3 rounded-v2 px-3 text-v2-13 transition-colors';

const HotelNavRow: React.FC<{
  item: HotelNavItem;
  propertyId: number | null;
  pathname: string;
  count?: number;
  onNavigate?: () => void;
}> = ({ item, propertyId, pathname, count, onNavigate }) => {
  const active = propertyId !== null && item.isActive(pathname, propertyId);
  const body = (
    <>
      <span
        className={clsx(
          'shrink-0',
          active ? 'text-v2-accent' : 'text-v2-fg-3 group-hover:text-v2-fg-2',
        )}
      >
        {item.icon}
      </span>
      <span className="flex-1 truncate">{item.label}</span>
      {/* The badge's own name lands in the link's: "Stays 3 on hold". */}
      {count !== undefined && <NavCount value={count} what={item.countWhat} />}
    </>
  );
  if (propertyId === null) {
    // No property yet: keep the row's shape, drop its link.
    return (
      <span
        role="link"
        aria-disabled="true"
        className={clsx(rowClass, 'font-medium text-v2-fg-4')}
      >
        {body}
      </span>
    );
  }
  return (
    <Link
      to={item.to(propertyId)}
      onClick={onNavigate}
      aria-current={active ? 'page' : undefined}
      className={clsx(
        rowClass,
        active
          ? 'bg-v2-accent-soft/20 font-semibold text-v2-fg before:absolute before:-left-3 before:bottom-2 before:top-2 before:w-[3px] before:rounded-r-full before:bg-v2-accent'
          : 'font-medium text-v2-fg-2 hover:bg-v2-sunken/70 hover:text-v2-fg',
      )}
    >
      {body}
    </Link>
  );
};

/**
 * The hotel shell's own user menu (IA Q3 (b)): Profile (inside this shell),
 * appearance, sign out. No "Usage & billing" until hotel billing exists.
 */
const HotelUserMenu: React.FC<{ onNavigate?: () => void }> = ({
  onNavigate,
}) => {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const anchor = useRef<HTMLButtonElement>(null);
  const name = `${user?.first_name ?? ''} ${user?.last_name ?? ''}`.trim();

  const go = (path: string) => {
    setOpen(false);
    onNavigate?.();
    navigate(path);
  };

  return (
    <div className="relative">
      <div className="v2-inverse border-t border-v2-line px-3 py-2.5">
        <button
          ref={anchor}
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-haspopup="menu"
          aria-expanded={open}
          className="flex h-11 w-full items-center gap-2.5 rounded-v2 px-2 text-left hover:bg-v2-sunken"
        >
          <Avatar name={name} />
          <span className="min-w-0 flex-1">
            <span className="block truncate text-v2-13 font-semibold leading-4 text-v2-fg">
              {name || user?.email || '—'}
            </span>
            <span className="block truncate text-v2-11 leading-4 text-v2-fg-3">
              {user?.is_platform_admin ? 'Platform admin' : user?.role || ''}
            </span>
          </span>
          <IconChevronDown className="text-v2-fg-3" />
        </button>
      </div>
      <Menu
        open={open}
        onClose={() => setOpen(false)}
        anchorRef={anchor}
        className="bottom-14 left-3 mt-0"
        width={208}
      >
        <MenuLabel>{user?.email}</MenuLabel>
        <MenuItem onClick={() => go(HOTEL_PROFILE_PATH)}>Profile</MenuItem>
        <MenuItem
          onClick={() => {
            setOpen(false);
            toggleTheme();
          }}
          leading={
            theme === 'dark' ? <IconSun size={14} /> : <IconMoon size={14} />
          }
        >
          {theme === 'dark' ? 'Light appearance' : 'Dark appearance'}
        </MenuItem>
        <MenuSeparator />
        <MenuItem
          tone="danger"
          onClick={() => {
            setOpen(false);
            logout();
            navigate('/signin');
          }}
        >
          Sign out
        </MenuItem>
      </Menu>
    </div>
  );
};
